(() => {
  'use strict';
  if (customElements.get('category-261010-catalog')) return;

  const previewKeys = ['preview_theme_id', 'preview_key', '_fd', 'pb', 'view'];
  const filterOptionLimit = 4;

  // Shopify still filters the complete collection. Only this section is replaced.
  class DzHolidayCatalog extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      if (!this.querySelector('[data-dh-filter-form]')) return;
      this.initialized = true;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.media = window.matchMedia('(max-width: 767px)');
      this.bindForm(new URL(window.location.href));
      this.renderedUrl = window.location.href;

      this.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
        if (input.form !== this.form) return;
        if (input instanceof HTMLInputElement && input.type === 'number') return;
        if (input.matches('[data-dh-discount-tier]')) {
          this.discountValues = input.value ? [input.value] : [];
          this.closeDiscounts(true);
        }
        this.form.requestSubmit();
      }, { signal });

      this.addEventListener('submit', (event) => {
        if (event.target !== this.form || event.defaultPrevented || !this.canRender()) return;
        event.preventDefault();
        const url = new URL(this.form.action, window.location.href);
        url.search = new URLSearchParams(new FormData(this.form)).toString();
        url.searchParams.delete('page');
        this.renderPage(url);
      }, { signal });

      this.addEventListener('click', (event) => {
        const showMore = event.target.closest('[data-dh-show-more]');
        if (showMore && this.contains(showMore)) {
          const group = showMore.closest('[data-dh-filter-param]');
          const state = this.filterValueStates?.get(group?.dataset.dhFilterParam);
          if (state) {
            state.expanded = !state.expanded;
            this.updateFilterLimit(group, state);
          }
          return;
        }
        const discountToggle = event.target.closest('[data-dh-discount-menu] > summary');
        if (discountToggle && this.contains(discountToggle) && this.media.matches) {
          if (!discountToggle.parentElement.open) this.setFiltersOpen(false);
          return;
        }
        const filterToggle = event.target.closest('[data-dh-mobile-filters]');
        if (filterToggle && this.contains(filterToggle) && this.media.matches) {
          this.closeDiscounts();
          this.setFiltersOpen(!this.querySelector('[data-dh-refine]')?.open);
          return;
        }
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a[data-dh-clear-filters], a.dh-active-filter, a[data-dh-quick-filter], .dh-native-pagination a');
        if (!link || !this.contains(link) || link.hasAttribute('download') || (link.target && link.target !== '_self') || !this.canRender()) return;
        const url = this.contextUrl(link.href);
        if (url.origin !== window.location.origin || url.pathname !== new URL(this.form.action).pathname) return;
        event.preventDefault();
        this.renderPage(url, { scroll: !!link.closest('.dh-native-pagination') });
      }, { signal });

      window.addEventListener('popstate', () => {
        const url = new URL(window.location.href);
        const rendered = new URL(this.renderedUrl);
        if (url.pathname !== rendered.pathname) return;
        if (url.search !== rendered.search || this.requestController) {
          this.renderPage(url, { history: false });
        }
      }, { signal });
      this.media.addEventListener('change', () => this.syncLayout(true), { signal });
      document.addEventListener('click', (event) => {
        const menu = this.querySelector('[data-dh-discount-menu]');
        if (menu && !menu.contains(event.target)) this.closeDiscounts();
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.media.matches && this.querySelector('[data-dh-discount-menu]')?.open) {
          event.preventDefault();
          this.closeDiscounts(true);
        }
      }, { signal });
    }

    canRender() {
      return !!(this.dataset.sectionId && window.fetch && window.history.pushState);
    }

    contextUrl(href, context = new URL(window.location.href)) {
      const url = new URL(href, context);
      previewKeys.forEach((key) => {
        if (context.searchParams.has(key)) url.searchParams.set(key, context.searchParams.get(key));
      });
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      return url;
    }

    bindForm(url) {
      this.formController?.abort();
      this.formController = new AbortController();
      this.form = this.querySelector('[data-dh-filter-form]');
      const { signal } = this.formController;
      const discountParam = this.dataset.discountParam;
      this.discountValues = discountParam ? url.searchParams.getAll(discountParam).filter(Boolean) : [];
      this.form.querySelectorAll('[data-dh-preview]').forEach((input) => input.remove());
      previewKeys.forEach((key) => {
        if (!url.searchParams.has(key)) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = url.searchParams.get(key);
        input.dataset.dhPreview = '';
        this.form.appendChild(input);
      });
      this.form.action = this.dataset.collectionUrl + '#dh-products';
      this.querySelectorAll('a[data-dh-clear-filters], a.dh-active-filter, a[data-dh-quick-filter], .dh-native-pagination a').forEach((link) => {
        link.href = this.contextUrl(link.href, url).href;
      });
      this.form.addEventListener('formdata', (event) => {
        // Keep exact incoming discount conditions until a tier button changes them.
        // A button submits every integer in its range, using Shopify's native OR filter.
        if (discountParam) {
          event.formData.delete(discountParam);
          this.discountValues.forEach((value) => event.formData.append(discountParam, value));
        }
        const filterNames = new Set(Array.from(event.formData.keys()).filter((name) => name.startsWith('filter.')));
        filterNames.forEach((name) => {
          const values = event.formData.getAll(name).filter((value) => value !== '');
          event.formData.delete(name);
          values.forEach((value) => event.formData.append(name, value));
        });
      }, { signal });
      const menu = this.querySelector('[data-dh-discount-menu]');
      menu?.addEventListener('toggle', () => {
        if (this.media.matches && menu.open) this.setFiltersOpen(false);
      }, { signal });
      this.setupFilterValues();
      this.classList.add('dh--enhanced');
      this.syncLayout(true);
    }

    setupFilterValues() {
      // Page-lifetime state: AJAX, Clear and history never recalculate known ranks.
      this.filterValueStates ||= new Map();
      this.filterLabelCollator ||= new Intl.Collator(undefined, { sensitivity: 'base' });
      this.querySelectorAll('[data-dh-filter-values]').forEach((list) => {
        const group = list.closest('[data-dh-filter-param]');
        if (!group) return;
        const key = group.dataset.dhFilterParam;
        let state = this.filterValueStates.get(key);
        if (!state) {
          state = { order: new Map(), expanded: false };
          this.filterValueStates.set(key, state);
        }
        const options = Array.from(list.children).filter((element) => element.matches('.dh-filter-option')).map((element, index) => ({
          element, index, value: element.querySelector('input').value,
          count: Number(element.dataset.dhFilterCount) || 0,
          label: element.dataset.dhFilterLabel || '',
        }));
        const additions = options.filter((option) => !state.order.has(option.value));
        additions.sort((a, b) => b.count - a.count || this.filterLabelCollator.compare(a.label, b.label) || a.index - b.index);
        // New values go last; temporarily absent values retain their saved rank.
        additions.forEach((option) => state.order.set(option.value, state.order.size));
        options.sort((a, b) => state.order.get(a.value) - state.order.get(b.value) || a.index - b.index);
        list.append(...options.map((option) => option.element));
        this.updateFilterLimit(group, state);
      });
    }

    updateFilterLimit(group, state) {
      const list = group.querySelector('[data-dh-filter-values]');
      if (!list) return;
      const options = Array.from(list.children).filter((element) => element.matches('.dh-filter-option'));
      options.forEach((element, index) => { element.hidden = !state.expanded && index >= filterOptionLimit; });
      const button = group.querySelector('[data-dh-show-more]');
      if (button) {
        button.hidden = options.length <= filterOptionLimit;
        button.setAttribute('aria-expanded', String(state.expanded));
        button.textContent = state.expanded ? button.dataset.showLess : button.dataset.showMore;
      }
    }

    syncLayout(reset = false) {
      const layout = this.querySelector('.dh-shop__layout');
      const main = this.querySelector('.dh-shop__main');
      const sidebar = this.querySelector('.dh-sidebar');
      const toolbar = this.querySelector('.dh-toolbar');
      if (sidebar && main && toolbar && layout) {
        if (this.media.matches) toolbar.insertAdjacentElement('afterend', sidebar);
        else layout.insertBefore(sidebar, main);
      }
      const results = this.querySelector('.dh-results-bar');
      const sort = this.querySelector('.dh-sort');
      if (sort && toolbar && results) {
        const destination = this.media.matches ? toolbar : results;
        if (sort.parentElement !== destination) {
          destination.insertBefore(sort, destination.querySelector('noscript'));
        }
      }
      const refine = this.querySelector('[data-dh-refine]');
      const menu = this.querySelector('[data-dh-discount-menu]');
      if (reset) {
        if (refine) refine.open = !this.media.matches;
        if (menu) menu.open = !this.media.matches;
      }
      sidebar?.classList.toggle('dh-sidebar--mobile-open', !!refine?.open);
      this.querySelector('[data-dh-mobile-filters]')?.setAttribute('aria-expanded', String(!!refine?.open));
    }

    setFiltersOpen(open) {
      const refine = this.querySelector('[data-dh-refine]');
      if (refine) refine.open = open;
      this.syncLayout();
    }

    closeDiscounts(returnFocus = false) {
      if (!this.media.matches) return;
      const menu = this.querySelector('[data-dh-discount-menu]');
      if (!menu?.open) return;
      menu.open = false;
      if (returnFocus) menu.querySelector('summary')?.focus({ preventScroll: true });
    }

    captureView() {
      const active = this.contains(document.activeElement) ? document.activeElement : null;
      const group = active?.closest('[data-dh-filter-param]');
      return {
        refineOpen: this.querySelector('[data-dh-refine]')?.open,
        discountOpen: this.querySelector('[data-dh-discount-menu]')?.open,
        groups: new Map(Array.from(this.querySelectorAll('[data-dh-filter-param]'), (element) => [element.dataset.dhFilterParam, element.open])),
        sidebarScroll: this.querySelector('.dh-sidebar')?.scrollTop || 0,
        quickScroll: this.querySelector('.dh-quick-filters')?.scrollLeft || 0,
        focus: active ? {
          id: active.id, name: active.name, value: active.value, type: active.type,
          group: group?.dataset.dhFilterParam,
          summary: active.tagName === 'SUMMARY',
          moreGroup: active.matches('[data-dh-show-more]') ? group?.dataset.dhFilterParam : null,
          apply: active.matches('.dh-apply'), clear: active.matches('[data-dh-clear-filters]'),
        } : null,
      };
    }

    restoreView(view, scroll) {
      const refine = this.querySelector('[data-dh-refine]');
      if (refine) refine.open = view.refineOpen ?? !this.media.matches;
      const menu = this.querySelector('[data-dh-discount-menu]');
      if (menu) menu.open = this.media.matches ? view.discountOpen ?? false : true;
      this.syncLayout();
      this.querySelectorAll('[data-dh-filter-param]').forEach((element) => {
        if (view.groups.has(element.dataset.dhFilterParam)) element.open = view.groups.get(element.dataset.dhFilterParam);
      });
      const sidebar = this.querySelector('.dh-sidebar');
      if (sidebar) sidebar.scrollTop = view.sidebarScroll;
      const quickFilters = this.querySelector('.dh-quick-filters');
      if (quickFilters) quickFilters.scrollLeft = view.quickScroll || 0;
      const focus = view.focus;
      let target;
      if (focus?.moreGroup != null) {
        const group = Array.from(this.querySelectorAll('[data-dh-filter-param]')).find((element) => element.dataset.dhFilterParam === focus.moreGroup);
        target = group?.querySelector('[data-dh-show-more]');
        if (target?.hidden) target = group.querySelector('summary');
      } else if (focus?.name) {
        target = Array.from(this.querySelectorAll('input, select')).find((element) => element.name === focus.name && element.type === focus.type && (element.tagName === 'SELECT' || element.value === focus.value));
      } else if (focus?.id) {
        target = this.querySelector('#' + CSS.escape(focus.id));
      } else if (focus?.summary) {
        const detail = focus.group ? Array.from(this.querySelectorAll('[data-dh-filter-param]')).find((element) => element.dataset.dhFilterParam === focus.group) : refine;
        target = detail?.querySelector('summary');
      } else if (focus?.apply || focus?.clear) {
        target = this.querySelector(focus.apply ? '.dh-apply' : '[data-dh-clear-filters]');
      }
      if (target && (target.disabled || target.closest('[hidden]') || !target.getClientRects().length)) {
        const group = target.closest('[data-dh-filter-param]');
        const more = group?.querySelector('[data-dh-show-more]');
        target = more && !more.hidden ? more : group?.querySelector('summary');
        if (target && !target.getClientRects().length) target = null;
      }
      if (scroll || (focus && !target)) {
        target = this.querySelector('.dh-status');
        if (target) target.tabIndex = -1;
      }
      target?.focus({ preventScroll: true });
      if (scroll) {
        const main = this.querySelector('.dh-shop__main');
        const offset = parseFloat(getComputedStyle(this).getPropertyValue('--dh-sticky-offset')) || 100;
        window.scrollTo({ top: Math.max(0, main.getBoundingClientRect().top + window.scrollY - offset), behavior: 'instant' });
      }
    }

    setBusy(busy) {
      const status = this.querySelector('.dh-status');
      if (busy && !this.classList.contains('dh--updating')) this.statusText = status?.textContent;
      this.classList.toggle('dh--updating', busy);
      this.setAttribute('aria-busy', String(busy));
      if (status && busy) status.textContent = 'Loading products…';
      if (status && !busy && status.textContent === 'Loading products…') status.textContent = this.statusText || '';
    }

    async renderPage(url, { history = true, scroll = false } = {}) {
      this.requestController?.abort();
      const controller = new AbortController();
      this.requestController = controller;
      const requestId = this.requestId = (this.requestId || 0) + 1;
      const requestUrl = new URL(url.href);
      requestUrl.hash = '';
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
      let timedOut = false;
      const timeout = window.setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
      this.setBusy(true);
      try {
        const response = await fetch(requestUrl.href, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Section response: ' + response.status);
        const html = await response.text();
        if (requestId !== this.requestId || !this.isConnected) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incoming = Array.from(parsed.querySelectorAll('category-261010-catalog')).find((element) => element.dataset.sectionId === this.dataset.sectionId);
        const shop = incoming?.querySelector('.dh-shop');
        if (!shop || !shop.querySelector('[data-dh-filter-form]')) throw new Error('Catalog section missing from response');
        const view = this.captureView();
        // Importing nodes lets existing product-card custom elements reconnect.
        // Do not execute returned scripts or reload unrelated sections/assets.
        this.replaceChildren(document.importNode(shop, true));
        this.bindForm(url);
        this.setBusy(false);
        this.restoreView(view, scroll);
        if (history && url.href !== window.location.href) window.history.pushState(window.history.state, '', url.href);
        this.renderedUrl = url.href;
        this.dispatchEvent(new CustomEvent('category-261010:updated', {
          bubbles: true, detail: { sectionId: this.dataset.sectionId, url: url.href },
        }));
      } catch (error) {
        if (requestId !== this.requestId || !this.isConnected || (error.name === 'AbortError' && !timedOut)) return;
        // A failed request must not leave unusable filters or stale results.
        console.warn('[category-261010] Section update failed; loading the collection page.', error);
        window.location.assign(url.href);
      } finally {
        window.clearTimeout(timeout);
        if (requestId === this.requestId) {
          this.requestController = null;
          this.setBusy(false);
        }
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.formController?.abort();
      this.requestController?.abort();
      this.setBusy(false);
      this.querySelectorAll('[data-dh-preview]').forEach((input) => input.remove());
      this.initialized = false;
    }
  }
  customElements.define('category-261010-catalog', DzHolidayCatalog);
})();
